import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { waLink, CATEGORIES } from "@/lib/brand";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] } }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="inicio" ref={ref} className="relative bg-cream overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 grid lg:grid-cols-2 gap-10 items-center pt-14 pb-20 lg:pt-16 lg:pb-24">
        {/* Text */}
        <div className="relative z-10 order-2 lg:order-1">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="text-xs uppercase tracking-luxe text-gold mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-gold inline-block" /> Confeitaria Artesanal
          </motion.p>

          <h1 className="font-serif leading-[0.9] text-choco">
            <span className="line-mask"><motion.span className="block text-6xl sm:text-7xl lg:text-8xl font-medium" variants={lineReveal} custom={0} initial="hidden" animate="show">Doces que</motion.span></span>
            <span className="line-mask mt-1"><motion.span className="block font-script text-7xl sm:text-8xl lg:text-9xl text-choco/90 leading-[1]" variants={lineReveal} custom={1} initial="hidden" animate="show">Encantam</motion.span></span>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 max-w-md text-base sm:text-lg text-muted-foreground leading-relaxed">
            Momentos especiais merecem <span className="text-choco font-semibold">sabores inesquecíveis.</span>
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <a href={waLink()} data-testid="hero-cta-encomenda"
               className="group inline-flex items-center gap-3 rounded-full bg-choco text-cream px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-gold transition-colors duration-300">
              Faça sua encomenda
              <MessageCircle size={18} className="group-hover:rotate-12 transition-transform" />
            </a>
            <a href="#categorias" className="inline-flex items-center gap-2 text-sm font-medium text-choco/70 hover:text-choco transition-colors">
              <ArrowDown size={16} /> Ver categorias
            </a>
          </motion.div>
        </div>

        {/* Image */}
        <div className="relative order-1 lg:order-2 h-[380px] sm:h-[480px] lg:h-[620px]">
          <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 rounded-[2rem] overflow-hidden shadow-2xl">
            <img src={CATEGORIES[0].image} alt="Bolo artesanal Deco Sweet" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-choco/30 to-transparent" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
